import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ACCOUNT_DELETION_CONFIRMATION,
  APP_CURRENCY,
  CategoryKind,
  type AppCurrency,
  type Category,
} from '@financial/shared';
import { useDeleteAccount, useProfile, useUpdateProfile } from '../features/profile/useProfile';
import {
  useCategories,
  useCreateCategory,
  useDeleteCategory,
} from '../features/categories/useCategories';
import { getApiErrorMessage } from '../lib/errors';
import { Field } from '../components/ui/Field';
import { GuidedPageHeader } from '../components/onboarding/GuidedPageHeader';
import { HelperText } from '../components/ui/Primitives';
import { Modal } from '../components/ui/Modal';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { useChangePassword } from '../features/auth/useAuth';
import { useHealth } from '../features/health/useHealth';
import { useAuthStore } from '../store/auth';

export function SettingsPage() {
  return (
    <div className="reference-page w-full">
      <GuidedPageHeader
        section="settings"
        title="Ajustes"
        description="Estos datos alimentan tus sugerencias y límites; actualízalos cuando cambie tu situación."
      />
      <div>
        <div className="system-page mx-auto max-w-3xl space-y-6">
          <ProfileSection />
          <CategoriesSection />
          <AboutSection />
          <SecuritySection />
          <AccountDeletionSection />
        </div>
      </div>
    </div>
  );
}

function AccountDeletionSection() {
  const navigate = useNavigate();
  const clearSession = useAuthStore((s) => s.clear);
  const deletion = useDeleteAccount();
  const [open, setOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');

  const close = () => {
    if (deletion.isPending) return;
    setOpen(false);
    setCurrentPassword('');
    setConfirmation('');
    deletion.reset();
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    deletion.mutate(
      { currentPassword, confirmation: confirmation as typeof ACCOUNT_DELETION_CONFIRMATION },
      {
        onSuccess: () => {
          clearSession();
          navigate('/login', { replace: true, state: { accountDeleted: true } });
        },
      },
    );
  };

  return (
    <section className="rounded-2xl border border-red-200 bg-red-50/50 p-5">
      <h2 className="text-sm font-semibold text-red-900">Eliminar cuenta</h2>
      <p className="mt-2 text-sm leading-6 text-red-900/80">
        Borra de forma definitiva tus cuentas, movimientos, tarjetas, metas, presupuestos y demás
        datos de la app. No cancela una futura suscripción Plus: esa gestión se mostrará aquí cuando
        exista.
      </p>
      <p className="mt-2 text-xs leading-5 text-red-800/80">
        Los respaldos protegidos se eliminan conforme a su ciclo normal de restauración.
      </p>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="tap-target mt-4 rounded-lg border border-red-300 bg-white px-4 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-100"
      >
        Eliminar mi cuenta
      </button>

      <Modal open={open} title="¿Eliminar tu cuenta?" onClose={close}>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="rounded-xl bg-red-50 p-3 text-sm leading-6 text-red-900">
            Esta acción es permanente. Se cerrará tu sesión y no podremos recuperar tus datos de la
            aplicación.
          </div>
          <Field
            id="delete-account-password"
            label="Contraseña actual"
            type="password"
            autoComplete="current-password"
            required
            minLength={8}
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            autoFocus
          />
          <Field
            id="delete-account-confirmation"
            label={`Escribe ${ACCOUNT_DELETION_CONFIRMATION}`}
            required
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            autoComplete="off"
          />
          {deletion.isError && (
            <p role="alert" className="text-sm text-expense">
              {getApiErrorMessage(deletion.error)}
            </p>
          )}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={close}
              disabled={deletion.isPending}
              className="button button-secondary w-full sm:w-auto"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={deletion.isPending || confirmation !== ACCOUNT_DELETION_CONFIRMATION}
              className="button button-danger w-full sm:w-auto"
            >
              {deletion.isPending ? 'Eliminando…' : 'Eliminar cuenta definitivamente'}
            </button>
          </div>
        </form>
      </Modal>
    </section>
  );
}

function SecuritySection() {
  const navigate = useNavigate();
  const clearSession = useAuthStore((s) => s.clear);
  const changePassword = useChangePassword();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (newPassword !== newPasswordConfirm) return;
    changePassword.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          // El API invalida el refresh token; limpiamos también esta sesión de inmediato.
          clearSession();
          navigate('/login', { replace: true, state: { passwordChanged: true } });
        },
      },
    );
  };

  return (
    <section className="ui-card" aria-labelledby="security-title">
      <h2 id="security-title" className="text-sm font-semibold text-slate-700">
        Seguridad
      </h2>
      <details className="group mt-3">
        <summary className="flex min-h-14 cursor-pointer list-none items-center justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 marker:content-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2">
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-slate-800 group-open:hidden">
              Cambiar contraseña
            </span>
            <span className="hidden text-sm font-semibold text-slate-800 group-open:block">
              Ocultar cambio de contraseña
            </span>
            <span className="mt-1 block text-xs leading-5 text-slate-500">
              Al guardarla, cerraremos tu sesión y tendrás que iniciar de nuevo.
            </span>
          </span>
          <span className="flex shrink-0 items-center text-slate-700">
            <span
              aria-hidden
              className="text-base text-slate-400 transition-transform group-open:rotate-180"
            >
              ⌄
            </span>
          </span>
        </summary>
        <div className="mt-4 border-t border-slate-100 pt-4">
          <form onSubmit={onSubmit} className="space-y-4">
            <Field
              id="current-password"
              label="Contraseña actual"
              type="password"
              autoComplete="current-password"
              required
              minLength={8}
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
            <Field
              id="new-password-settings"
              label="Nueva contraseña"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
            <Field
              id="new-password-settings-confirm"
              label="Confirmar nueva contraseña"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={newPasswordConfirm}
              onChange={(event) => setNewPasswordConfirm(event.target.value)}
            />
            {newPasswordConfirm && newPassword !== newPasswordConfirm && (
              <p role="alert" className="text-sm text-expense">
                Las contraseñas no coinciden.
              </p>
            )}
            {changePassword.isError && (
              <p role="alert" className="text-sm text-expense">
                {getApiErrorMessage(changePassword.error)}
              </p>
            )}
            <button
              type="submit"
              disabled={changePassword.isPending || newPassword !== newPasswordConfirm}
              className="tap-target w-full rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60 sm:w-auto"
            >
              {changePassword.isPending ? 'Cambiando…' : 'Guardar nueva contraseña'}
            </button>
          </form>
        </div>
      </details>
    </section>
  );
}

function ProfileSection() {
  const profileQ = useProfile();
  const update = useUpdateProfile();
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState<AppCurrency>(APP_CURRENCY);
  const [baseIncome, setBaseIncome] = useState('');
  const [savingsRate, setSavingsRate] = useState('');
  const [purchaseSafetyBuffer, setPurchaseSafetyBuffer] = useState('');
  const [aiConsentGranted, setAiConsentGranted] = useState(false);

  useEffect(() => {
    if (profileQ.data) {
      setName(profileQ.data.name);
      setCurrency(profileQ.data.defaultCurrency);
      const profile = profileQ.data.savingsProfiles[profileQ.data.defaultCurrency] ?? {
        baseIncome: 0,
        targetSavingsRate: 0,
        purchaseSafetyBuffer: 0,
      };
      setBaseIncome(String(profile.baseIncome ?? 0));
      setSavingsRate(String(Math.round((profile.targetSavingsRate ?? 0) * 100)));
      setPurchaseSafetyBuffer(String(profile.purchaseSafetyBuffer ?? 0));
      setAiConsentGranted(profileQ.data.aiConsentGranted);
    }
  }, [profileQ.data]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    update.mutate({
      name,
      defaultCurrency: currency,
      savingsProfile: {
        currency,
        baseIncome: Number(baseIncome) || 0,
        targetSavingsRate: Math.min(1, Math.max(0, (Number(savingsRate) || 0) / 100)),
        purchaseSafetyBuffer: Math.max(0, Number(purchaseSafetyBuffer) || 0),
      },
      aiConsentGranted,
    });
  };

  if (profileQ.isPending) {
    return (
      <section
        id="ahorro"
        className="ui-card scroll-mt-5"
        aria-busy="true"
        aria-label="Cargando perfil"
      >
        <h2 className="mb-3 text-sm font-semibold text-slate-700">Perfil</h2>
        <div className="space-y-4">
          <div className="h-[68px] animate-pulse rounded-xl bg-slate-100" />
          <div className="grid grid-cols-2 gap-3">
            <div className="h-[68px] animate-pulse rounded-xl bg-slate-100" />
            <div className="h-[68px] animate-pulse rounded-xl bg-slate-100" />
          </div>
        </div>
      </section>
    );
  }

  if (profileQ.isError || !profileQ.data) {
    return (
      <section id="ahorro" className="ui-card scroll-mt-5">
        <h2 className="text-sm font-semibold text-slate-700">Perfil</h2>
        <p role="alert" className="mt-2 text-sm text-expense">
          {getApiErrorMessage(
            profileQ.error,
            'No pudimos cargar tu perfil. Tus datos no se modificaron.',
          )}
        </p>
        <button
          type="button"
          onClick={() => void profileQ.refetch()}
          className="tap-target mt-3 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          Reintentar
        </button>
      </section>
    );
  }

  return (
    <section id="ahorro" className="ui-card scroll-mt-5">
      <h2 className="mb-3 text-sm font-semibold text-slate-700">Perfil</h2>
      <form onSubmit={onSubmit} className="space-y-4">
        <Field id="p-name" label="Nombre" value={name} onChange={(e) => setName(e.target.value)} />
        <p className="text-xs text-slate-600">Correo: {profileQ.data?.email}</p>
        <div
          id="asistente"
          className="scroll-mt-5 rounded-xl border border-slate-200 bg-slate-50 p-3"
        >
          <h3 className="text-sm font-semibold text-slate-800">Asistente de IA</h3>
          <label className="mt-2 flex min-h-11 cursor-pointer items-start gap-3 py-1 text-sm leading-5 text-slate-700">
            <input
              type="checkbox"
              checked={aiConsentGranted}
              onChange={(event) => setAiConsentGranted(event.target.checked)}
              className="mt-0.5 h-5 w-5 shrink-0"
            />
            <span>
              <strong>Permitir el asistente de IA</strong>
              <br />
              <span className="text-xs text-slate-500">
                Puede procesar la información mínima de tus finanzas registradas para responder. La
                prueba se ofrece una sola vez por cuenta. Si no la has usado, sus 14 días empiezan
                cuando guardas este permiso. Retirarlo no pausa ni reinicia el plazo. No procesa
                contraseñas, NIP, CVV ni adjuntos.
              </span>
            </span>
          </label>
        </div>
        <div>
          <label htmlFor="p-currency" className="mb-1 block text-sm font-medium text-slate-700">
            Moneda predeterminada
          </label>
          <select
            id="p-currency"
            value={currency}
            onChange={(event) => setCurrency(event.target.value as AppCurrency)}
            className="tap-target w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-base"
          >
            <option value="MXN">Pesos mexicanos (MXN)</option>
            <option value="USD">Dólares estadounidenses (USD)</option>
            <option value="CAD">Dólares canadienses (CAD)</option>
          </select>
          <p className="mt-1 text-xs leading-5 text-slate-600">
            Define la moneda inicial de vistas y formularios. No convierte los movimientos ni saldos
            que ya registraste.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            id="p-income"
            label={`Ingreso mensual estimado (${currency})`}
            type="number"
            inputMode="decimal"
            value={baseIncome}
            onChange={(e) => setBaseIncome(e.target.value)}
            helperText="Escribe un aproximado de lo que normalmente recibes al mes. No tiene que ser exacto y puedes cambiarlo después."
          />
          <Field
            id="p-rate"
            label="Ahorro objetivo mensual (%)"
            type="number"
            min="0"
            max="100"
            inputMode="decimal"
            value={savingsRate}
            onChange={(e) => setSavingsRate(e.target.value)}
            helperText="Si no sabes qué poner, prueba con 10% como punto de partida. Lo usamos para calcular tu límite de gasto sugerido y puedes cambiarlo después."
          />
        </div>
        <Field
          id="p-purchase-buffer"
          label={`Dinero libre mínimo después de una compra (${currency})`}
          type="number"
          min="0"
          inputMode="decimal"
          value={purchaseSafetyBuffer}
          onChange={(e) => setPurchaseSafetyBuffer(e.target.value)}
          helperText="Es un margen de tu presupuesto mensual, no dinero asignado a una cuenta o apartado. Lo usamos para evaluar compras o nuevas mensualidades; no mueve dinero ni modifica tus saldos o deudas."
        />
        {update.isError && (
          <p role="alert" className="text-sm text-expense">
            {getApiErrorMessage(update.error)}
          </p>
        )}
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={update.isPending}
            className="tap-target rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
          >
            {update.isPending ? 'Guardando…' : 'Guardar perfil'}
          </button>
          {update.isSuccess && <span className="text-sm text-income">Guardado ✓</span>}
        </div>
      </form>
    </section>
  );
}

function CategoriesSection() {
  const categoriesQ = useCategories();
  const create = useCreateCategory();
  const del = useDeleteCategory();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<CategoryKind>(CategoryKind.EXPENSE);
  const [icon, setIcon] = useState('');
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);

  const categories = categoriesQ.data ?? [];
  const expense = categories.filter((c) => c.kind === CategoryKind.EXPENSE);
  const income = categories.filter((c) => c.kind === CategoryKind.INCOME);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!name) return;
    create.mutate({ name, kind, icon: icon || undefined }, { onSuccess: () => setName('') });
  };

  return (
    <section className="ui-card">
      <h2 className="mb-3 text-sm font-semibold text-slate-700">Categorías</h2>
      <p className="mb-3 text-xs leading-5 text-slate-500">
        Categorizar tus movimientos mejora las alertas y hace que los gráficos sean más útiles.
      </p>

      <div className="grid gap-4 sm:grid-cols-2">
        <CategoryList title="Gastos" items={expense} onDelete={setCategoryToDelete} />
        <CategoryList title="Ingresos" items={income} onDelete={setCategoryToDelete} />
      </div>

      <form
        onSubmit={onSubmit}
        className="mt-4 flex flex-wrap items-end gap-2 border-t border-slate-100 pt-4"
      >
        <input
          value={icon}
          onChange={(e) => setIcon(e.target.value)}
          placeholder="🍔"
          maxLength={2}
          className="tap-target w-14 rounded-lg border border-slate-300 bg-white px-2 py-2 text-center text-base"
          aria-label="Icono"
        />
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nueva categoría"
          className="tap-target min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-base"
          aria-label="Nombre de categoría"
        />
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value as CategoryKind)}
          className="tap-target rounded-lg border border-slate-300 bg-white px-3 py-2 text-base"
          aria-label="Tipo"
        >
          <option value={CategoryKind.EXPENSE}>Gasto</option>
          <option value={CategoryKind.INCOME}>Ingreso</option>
        </select>
        <button
          type="submit"
          disabled={create.isPending}
          className="tap-target rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
        >
          Agregar
        </button>
      </form>
      {create.isError && (
        <p role="alert" className="mt-2 text-sm text-expense">
          {getApiErrorMessage(create.error)}
        </p>
      )}
      <ConfirmDialog
        open={categoryToDelete !== null}
        title="¿Eliminar categoría?"
        description="La categoría dejará de estar disponible. Los movimientos existentes conservarán sus montos."
        itemLabel={
          categoryToDelete
            ? `${categoryToDelete.icon ? `${categoryToDelete.icon} ` : ''}${categoryToDelete.name}`
            : undefined
        }
        pending={del.isPending}
        error={del.isError ? getApiErrorMessage(del.error) : undefined}
        onClose={() => {
          setCategoryToDelete(null);
          del.reset();
        }}
        onConfirm={() =>
          categoryToDelete &&
          del.mutate(categoryToDelete.id, { onSuccess: () => setCategoryToDelete(null) })
        }
      />
    </section>
  );
}

function CategoryList({
  title,
  items,
  onDelete,
}: {
  title: string;
  items: Category[];
  onDelete: (category: Category) => void;
}) {
  return (
    <div>
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-600">{title}</h3>
      <ul className="space-y-1">
        {items.map((c) => (
          <li
            key={c.id}
            className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5 text-sm"
          >
            <span className="min-w-0 truncate">
              {c.icon ? `${c.icon} ` : ''}
              {c.name}
            </span>
            <button
              type="button"
              onClick={() => onDelete(c)}
              className="tap-target flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-slate-600 hover:bg-red-50 hover:text-expense"
              aria-label={`Eliminar ${c.name}`}
            >
              ×
            </button>
          </li>
        ))}
        {items.length === 0 && <li className="text-xs text-slate-600">Sin categorías.</li>}
      </ul>
    </div>
  );
}

function AboutSection() {
  const { data, isLoading, isError } = useHealth();
  const version = isLoading ? '…' : isError ? 'no disponible' : (data?.version ?? 'desconocida');

  return (
    <section className="ui-card">
      <h2 className="mb-3 text-sm font-semibold text-slate-700">Acerca de</h2>
      <dl className="space-y-2 text-sm">
        <div className="flex items-center justify-between gap-3">
          <dt className="text-slate-500">Versión</dt>
          <dd className="font-medium tabular-nums text-slate-700">{version}</dd>
        </div>
        <div className="flex items-center justify-between gap-3">
          <dt className="text-slate-500">Servicio</dt>
          <dd className="font-medium text-slate-700">
            {isLoading ? '…' : isError ? 'sin conexión' : 'en línea'}
          </dd>
        </div>
      </dl>
      <HelperText className="mt-3" tone={isError ? 'warning' : 'default'}>
        {isError
          ? 'No se pudo consultar el estado del servidor. Revisa tu conexión.'
          : 'Si algo se ve raro, este número identifica la versión que estás usando.'}
      </HelperText>
    </section>
  );
}
